import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MATCH_AUTO } from '../../constants/matching-algorithms'
import { permissionsService } from '../../services/permissions.service'
import { tagService } from '../../services/tag.service'
import { Tag } from '../../types'
import { getMatchingAlgorithms, isPatternRequired } from '../../utils/matchingAlgorithms'
import { CheckboxInput, ColorInput, EditDialog, FormField, SelectInput, TextInput } from '../common/EditDialog'
import { PermissionsForm, PermissionsFormValue } from '../common/PermissionsForm'

interface TagEditDialogProps {
  object?: Tag | null
  isCreating: boolean
  onSuccess: () => void
  onError: (error: any) => void
  onClose: () => void
}

export const TagEditDialog: React.FC<TagEditDialogProps> = ({ object, isCreating, onSuccess, onError, onClose }) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState<Partial<Tag>>({})
  const [tags, setTags] = useState<Tag[]>([])

  useEffect(() => {
    loadTags()
    if (object) {
      // Map permissions from object to formData
      // Backend returns permissions as { view: {...}, change: {...} }
      // which we need to map to set_permissions for the form
      setFormData({
        ...object,
        owner: object.owner || null,
        set_permissions: object.permissions || {
          view: { users: [], groups: [] },
          change: { users: [], groups: [] },
        },
      })
    } else {
      setFormData({
        name: '',
        color: generateRandomColor(),
        text_color: '#ffffff',
        is_inbox_tag: false,
        parent: null,
        matching_algorithm: MATCH_AUTO,
        match: '',
        is_insensitive: true,
        owner: null,
        set_permissions: {
          view: { users: [], groups: [] },
          change: { users: [], groups: [] },
        },
      })
    }
  }, [object])

  const loadTags = async () => {
    try {
      const tags = await tagService.listAll()
      setTags(tags)
    } catch (error) {}
  }

  const generateRandomColor = (): string => {
    const colors = [
      '#FF6B6B',
      '#4ECDC4',
      '#45B7D1',
      '#96CEB4',
      '#FFEAA7',
      '#DDA0DD',
      '#98D8C8',
      '#F7DC6F',
      '#BB8FCE',
      '#85C1E9',
      '#F8C471',
      '#82E0AA',
      '#F1948A',
      '#85C1E9',
      '#D7BDE2',
    ]
    return colors[Math.floor(Math.random() * colors.length)]
  }

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const validateForm = (data: Partial<Tag>): string[] => {
    const errors: string[] = []

    if (!data.name || data.name.trim() === '') {
      errors.push('name:Name is required')
    }

    if (!data.color || data.color.trim() === '') {
      errors.push('color:Color is required')
    }

    return errors
  }

  const getFormFields = (errors?: Record<string, string>) => (
    <>
      <FormField label={t('name')} required error={errors?.name}>
        <TextInput
          value={formData.name || ''}
          onChange={(value) => handleInputChange('name', value)}
          placeholder={t('enter_name')}
          required
          autoComplete="off"
          error={errors?.name}
        />
      </FormField>

      <FormField label={t('color')} required error={errors?.color}>
        <ColorInput value={formData.color || '#000000'} onChange={(value) => handleInputChange('color', value)} error={errors?.color} />
      </FormField>

      <FormField label={t('parent')}>
        <SelectInput
          value={formData.parent}
          onChange={(value) => handleInputChange('parent', value)}
          options={tags
            .filter((tag) => tag.id !== object?.id) // Don't allow self as parent
            .map((tag) => ({ value: tag.id, label: tag.name }))}
          placeholder={t('select_parent')}
          allowNull
        />
      </FormField>

      <FormField label={t('inbox_tag')}>
        <CheckboxInput
          checked={formData.is_inbox_tag || false}
          onChange={(checked) => handleInputChange('is_inbox_tag', checked)}
          label={t('inbox_tag')}
          hint={t('inbox_tag_hint')}
        />
      </FormField>

      <FormField label={t('matching_algorithm')}>
        <SelectInput
          value={formData.matching_algorithm}
          onChange={(value) => handleInputChange('matching_algorithm', value !== null ? parseInt(value) : null)}
          options={getMatchingAlgorithms(t)}
        />
      </FormField>

      {formData.matching_algorithm !== undefined && isPatternRequired(formData.matching_algorithm) && (
        <>
          <FormField label={t('matching_pattern')}>
            <TextInput
              value={formData.match || ''}
              onChange={(value) => handleInputChange('match', value)}
              placeholder={t('enter_matching_pattern')}
            />
          </FormField>

          <FormField label={t('case_insensitive')}>
            <CheckboxInput
              checked={formData.is_insensitive || false}
              onChange={(checked) => handleInputChange('is_insensitive', checked)}
              label={t('case_insensitive')}
            />
          </FormField>
        </>
      )}

      {(!object || permissionsService.currentUserOwnsObject(object)) && (
        <PermissionsForm
          value={{
            owner: formData.owner || null,
            set_permissions: formData.set_permissions || {
              view: { users: [], groups: [] },
              change: { users: [], groups: [] },
            },
          }}
          onChange={(permissionsValue: PermissionsFormValue) => {
            setFormData(
              (prev) =>
                ({
                  ...prev,
                  owner: permissionsValue.owner,
                  set_permissions: permissionsValue.set_permissions,
                }) as Partial<Tag>
            )
          }}
          accordion={true}
        />
      )}
    </>
  )

  const getTitle = () => {
    return isCreating ? t('create_new_tag') : t('edit_tag')
  }

  return (
    <EditDialog
      object={object}
      isCreating={isCreating}
      onSuccess={onSuccess}
      onError={onError}
      onClose={onClose}
      service={tagService}
      formData={formData}
      getFormFields={getFormFields}
      getTitle={getTitle}
      validateForm={validateForm}
    />
  )
}
